/**
 * エリアの境界の幾何（純粋関数）。サーバーの判定 `_area_at`（supabase/migrations/013_smooth_area.sql）と同じ式：
 *   F = dmin - k * ln( Σ exp(-(dᵢ - dmin) / k) )   （dᵢ はそのエリアの各地点までの距離、k = area_blend_m、k = 0 なら F = dmin）
 * F が一番小さいエリアに入れ、F ≤ area_reach_m なら中。地図の線はこの判定の境目を格子の等高線（marching squares）で取る。
 * 地点＝そのエリアのスポットと、場所が決まったお題（fixed_location）。
 */

export const DEFAULT_BLEND_M = 20;
export const DEFAULT_REACH_M = 80;

const EARTH_R = 6371008.8;

export interface Site {
  lat: number;
  lon: number;
}

export interface AreaSites {
  areaId: string;
  sites: Site[];
}

export type LatLon = [number, number];

export interface Projection {
  toXY(lat: number, lon: number): [number, number];
  toLatLon(x: number, y: number): LatLon;
}

/** 緯度経度を基準点まわりのメートル座標（x＝東、y＝北）に直す。数km以内なら誤差は無視できる */
export function makeProjection(lat0: number, lon0: number): Projection {
  const c = Math.cos((lat0 * Math.PI) / 180);
  const kx = (EARTH_R * c * Math.PI) / 180;
  const ky = (EARTH_R * Math.PI) / 180;
  return {
    toXY: (lat, lon) => [(lon - lon0) * kx, (lat - lat0) * ky],
    toLatLon: (x, y) => [lat0 + y / ky, lon0 + x / kx],
  };
}

/** なめらかに合成した距離 F。d は各地点までの距離（m） */
export function blendedDistance(d: readonly number[], k: number): number {
  if (d.length === 0) return Infinity;
  let dmin = Infinity;
  for (const v of d) if (v < dmin) dmin = v;
  if (k <= 0) return dmin;
  let s = 0;
  for (const v of d) s += Math.exp(-(v - dmin) / k);
  return dmin - k * Math.log(s);
}

export interface ProjectedArea {
  areaId: string;
  pts: [number, number][];
}

function fOf(a: ProjectedArea, x: number, y: number, k: number): number {
  const d = a.pts.map(([px, py]) => Math.hypot(px - x, py - y));
  return blendedDistance(d, k);
}

/** メートル座標の点がどのエリアか（サーバーの _area_at と同じ。測位誤差の上乗せなし）。外なら null */
export function areaAtXY(areas: readonly ProjectedArea[], x: number, y: number, k: number, reach: number): string | null {
  let best: string | null = null;
  let bf = Infinity;
  for (const a of areas) {
    const f = fOf(a, x, y, k);
    if (f < bf || (f === bf && best !== null && a.areaId < best)) {
      bf = f;
      best = a.areaId;
    }
  }
  return best !== null && bf <= reach ? best : null;
}

function allSites(areas: readonly AreaSites[]): Site[] {
  return areas.flatMap((a) => a.sites);
}

/** 全地点の重心を基準にした投影（computeAreaContours と同じもの） */
export function projectionFor(areas: readonly AreaSites[]): Projection | null {
  const s = allSites(areas);
  if (!s.length) return null;
  let lat = 0;
  let lon = 0;
  for (const p of s) {
    lat += p.lat;
    lon += p.lon;
  }
  return makeProjection(lat / s.length, lon / s.length);
}

export function projectAreas(areas: readonly AreaSites[], proj: Projection): ProjectedArea[] {
  return areas
    .filter((a) => a.sites.length > 0)
    .map((a) => ({ areaId: a.areaId, pts: a.sites.map((s) => proj.toXY(s.lat, s.lon)) }));
}

/** 緯度経度版（テストとデバッグ用） */
export function areaAt(areas: readonly AreaSites[], lat: number, lon: number, k = DEFAULT_BLEND_M, reach = DEFAULT_REACH_M): string | null {
  const proj = projectionFor(areas);
  if (!proj) return null;
  const [x, y] = proj.toXY(lat, lon);
  return areaAtXY(projectAreas(areas, proj), x, y, k, reach);
}

export interface ContourOptions {
  blendM?: number;
  reachM?: number;
  /** 格子の1マス（m）。既定 6 */
  cellM?: number;
}

/**
 * エリアごとの輪郭（閉じた多角形の集まり）を緯度経度で返す。
 * エリア A の場 G_A = max(F_A - reach, F_A - 他エリアの F の最小)。G_A ≤ 0 が A の中。G_A = 0 の等高線が
 * 外側の縁と隣のエリアとの境目を兼ねる（境目は両側の F が等しい1本のなめらかな線）。
 */
export function computeAreaContours(areas: readonly AreaSites[], opts: ContourOptions = {}): Map<string, LatLon[][]> {
  const out = new Map<string, LatLon[][]>();
  const k = opts.blendM ?? DEFAULT_BLEND_M;
  const reach = opts.reachM ?? DEFAULT_REACH_M;
  const proj = projectionFor(areas);
  if (!proj) return out;
  const pa = projectAreas(areas, proj);
  if (!pa.length) return out;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxN = 1;
  for (const a of pa) {
    maxN = Math.max(maxN, a.pts.length);
    for (const [x, y] of a.pts) {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }
  // F ≤ reach の点は、一番近い地点から reach + k·ln(n) 以内にある。縁がはみ出さない余白を取る
  const pad = reach + k * Math.log(maxN + 1) + 2 * (opts.cellM ?? 6) + 4;
  minX -= pad;
  minY -= pad;
  maxX += pad;
  maxY += pad;
  const cell = Math.max(opts.cellM ?? 6, Math.max(maxX - minX, maxY - minY) / 240);
  const nx = Math.ceil((maxX - minX) / cell) + 1;
  const ny = Math.ceil((maxY - minY) / cell) + 1;

  // 各エリアの F を格子の全点で求める
  const F = pa.map(() => new Float64Array(nx * ny));
  for (let j = 0; j < ny; j++) {
    const y = minY + j * cell;
    for (let i = 0; i < nx; i++) {
      const x = minX + i * cell;
      for (let a = 0; a < pa.length; a++) {
        const area = pa[a];
        const arr = F[a];
        if (area && arr) arr[j * nx + i] = fOf(area, x, y, k);
      }
    }
  }

  for (let a = 0; a < pa.length; a++) {
    const area = pa[a];
    const fa = F[a];
    if (!area || !fa) continue;
    const g = new Float64Array(nx * ny);
    for (let idx = 0; idx < g.length; idx++) {
      let other = Infinity;
      for (let b = 0; b < pa.length; b++) {
        if (b === a) continue;
        const v = F[b]?.[idx];
        if (v !== undefined && v < other) other = v;
      }
      const f = fa[idx] ?? Infinity;
      g[idx] = Math.max(f - reach, f - other);
    }
    const rings = marchingSquares(g, nx, ny).map((ring) => ring.map(([gx, gy]) => proj.toLatLon(minX + gx * cell, minY + gy * cell)));
    if (rings.length) out.set(area.areaId, rings);
  }
  return out;
}

/** 値 ≤ 0 を中とみなした格子の等高線（0 の線）。格子座標の閉じた折れ線を返す */
export function marchingSquares(g: ArrayLike<number>, nx: number, ny: number): [number, number][][] {
  const pts = new Map<string, [number, number]>();
  const adj = new Map<string, string[]>();
  const val = (i: number, j: number): number => g[j * nx + i] ?? 1;
  const inside = (i: number, j: number): boolean => val(i, j) <= 0;

  // 辺の交点（線形補間）。h＝横の辺 (i,j)-(i+1,j)、v＝縦の辺 (i,j)-(i,j+1)
  const edge = (kind: 'h' | 'v', i: number, j: number): string => {
    const key = `${kind}${i},${j}`;
    if (!pts.has(key)) {
      const a = val(i, j);
      const b = kind === 'h' ? val(i + 1, j) : val(i, j + 1);
      const t = a === b ? 0.5 : a / (a - b);
      pts.set(key, kind === 'h' ? [i + t, j] : [i, j + t]);
    }
    return key;
  };
  const push = (p: string, q: string): void => {
    const l = adj.get(p);
    if (l) l.push(q);
    else adj.set(p, [q]);
  };
  const link = (p: string, q: string): void => {
    push(p, q);
    push(q, p);
  };

  for (let j = 0; j < ny - 1; j++) {
    for (let i = 0; i < nx - 1; i++) {
      const c = (inside(i, j) ? 1 : 0) | (inside(i + 1, j) ? 2 : 0) | (inside(i + 1, j + 1) ? 4 : 0) | (inside(i, j + 1) ? 8 : 0);
      if (c === 0 || c === 15) continue;
      const top = edge('h', i, j);
      const bottom = edge('h', i, j + 1);
      const left = edge('v', i, j);
      const right = edge('v', i + 1, j);
      switch (c) {
        case 1:
        case 14:
          link(left, top);
          break;
        case 2:
        case 13:
          link(top, right);
          break;
        case 3:
        case 12:
          link(left, right);
          break;
        case 4:
        case 11:
          link(right, bottom);
          break;
        case 6:
        case 9:
          link(top, bottom);
          break;
        case 7:
        case 8:
          link(left, bottom);
          break;
        case 5:
        case 10: {
          // 対角どうしが中。中心の値の平均で、つながるか切れるかを決める
          const centerIn = (val(i, j) + val(i + 1, j) + val(i + 1, j + 1) + val(i, j + 1)) / 4 <= 0;
          const cutBD = (c === 5) === centerIn; // c=5(a,c中)で中心が中 → b・d の角を切り離す
          if (cutBD) {
            link(top, right);
            link(bottom, left);
          } else {
            link(left, top);
            link(right, bottom);
          }
          break;
        }
      }
    }
  }

  const seen = new Set<string>();
  const rings: [number, number][][] = [];
  for (const start of adj.keys()) {
    if (seen.has(start)) continue;
    const ring: [number, number][] = [];
    let prev: string | null = null;
    let cur: string | undefined = start;
    while (cur !== undefined && !seen.has(cur)) {
      seen.add(cur);
      const p = pts.get(cur);
      if (p) ring.push(p);
      const here: string = cur;
      const nb: string[] = adj.get(here) ?? [];
      const next: string | undefined = nb.find((n) => n !== prev && !seen.has(n));
      prev = here;
      cur = next;
    }
    if (ring.length >= 3) rings.push(ring);
  }
  return rings;
}

/** 点が輪の集まりの中か（偶奇則。穴があっても使える）。x, y は輪と同じ座標系 */
export function pointInRings(rings: readonly (readonly (readonly [number, number])[])[], x: number, y: number): boolean {
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const a = ring[i];
      const b = ring[j];
      if (!a || !b) continue;
      if (a[1] > y !== b[1] > y && x < ((b[0] - a[0]) * (y - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside;
    }
  }
  return inside;
}
